// Optional tabs for an entity mask. An input spec opts in by placing `type: 'tab'`
// markers in its (still flat) inputFields array; everything after a marker, up to
// the next one, belongs to that tab, and anything before the first marker stays
// visible above the tab bar.
//
// ⚠ Tabs are a PRESENTATION layer, deliberately. EntityMaskForm keys almost all
// per-field state by the field's index in the flat array -- its rules
// (`fieldValidations[index]`), its refs (`'ifield-' + index`), the search
// debouncers, tiedToPriorFieldCheckbox's `index - 1` -- so the array is never
// split. The form hides an inactive tab's fields with `v-show`, which keeps them
// MOUNTED. That is load-bearing: VForm validates only the inputs registered with
// it, so a lazily-rendered tab (v-window's default) would let a `required` field
// on a never-visited tab pass Save silently. For the same reason a hidden tab must
// never be expressed through `unrendered`: that flag skips validation outright
// (fieldValidations.ts) and can drop the value from the payload (dirtyFields.ts).

import type { TEntityMaskItem } from '~/types/models'
import { isEntityMaskData, isTab } from '~/types/models'

export interface IMaskTab {
  key: string
  label: string
}

export interface IMaskTabLayout {
  // In marker order.
  tabs: IMaskTab[]
  // Parallel to inputFields: the tab key an item belongs to, or null for an item
  // before the first marker (always shown). A marker maps to its own key.
  tabKeyByIndex: (string | null)[]
}

export function buildTabLayout(inputFields: TEntityMaskItem[]): IMaskTabLayout {
  const tabs: IMaskTab[] = []
  const tabKeyByIndex: (string | null)[] = []
  let currentKey: string | null = null
  for (const item of inputFields) {
    if (isTab(item)) {
      currentKey = item.name
      tabs.push({ key: item.name, label: item.label })
    }
    tabKeyByIndex.push(currentKey)
  }
  return { tabs, tabKeyByIndex }
}

// Whether the mask uses tabs at all. A mask without markers renders exactly as it
// always has.
export function hasTabs(layout: IMaskTabLayout): boolean {
  return layout.tabs.length > 0
}

// The tabs worth showing a header for: those with at least one rendered field.
// Layout-only items (subtitle / divider / spacer) do not count, so a tab left
// holding nothing but a subtitle -- because runOnChanged, showForRoutes or
// unrenderBasedOnState took its fields away -- disappears rather than opening
// onto an empty pane.
export function getVisibleTabs(
  layout: IMaskTabLayout,
  inputFields: TEntityMaskItem[]
): IMaskTab[] {
  const keysWithContent = new Set<string>()
  inputFields.forEach((item, index) => {
    const key = layout.tabKeyByIndex[index]
    if (typeof key === 'string' && isEntityMaskData(item) && !item.unrendered) {
      keysWithContent.add(key)
    }
  })
  return layout.tabs.filter((tab) => keysWithContent.has(tab.key))
}

// Keep the current tab while it is still visible; otherwise fall back to the
// first visible one (or null when there is none).
export function resolveActiveTabKey(
  currentKey: string | null,
  visibleTabs: IMaskTab[]
): string | null {
  if (
    currentKey !== null &&
    visibleTabs.some((tab) => tab.key === currentKey)
  ) {
    return currentKey
  }
  return visibleTabs[0]?.key ?? null
}

// Whether the item at `index` is on screen for the given active tab. Items before
// the first marker are always shown -- which, for a mask with no markers, is all
// of them.
export function isIndexInActiveTab(
  layout: IMaskTabLayout,
  index: number,
  activeTabKey: string | null
): boolean {
  const key = layout.tabKeyByIndex[index]
  return key === null || key === undefined || key === activeTabKey
}

// Which tabs currently hold a failing field, as tab keys (null = the always-shown
// area above the tabs).
//
// Computed from the rules and the values rather than read off the DOM, so it is
// reactive: as the user fixes a field on another tab, that tab's marker clears.
// `fieldValidations` is only populated from the moment Save is pressed
// (EntityMaskForm builds it at the top of trySave and empties it at open and
// after a successful save), so there is nothing to report before the user has
// asked to save -- matching when Vuetify itself starts showing errors.
//
// A rule that throws is treated as passing. Some rules assume a string
// (`value.length`) and are only attached when the field had a value when Save
// was pressed; a later clear would throw here, and a tab marker is not the place
// to surface that.
export function getTabKeysWithErrors({
  layout,
  inputFields,
  fieldValidations,
}: {
  layout: IMaskTabLayout
  inputFields: TEntityMaskItem[]
  fieldValidations: (Function[] | undefined)[]
}): (string | null)[] {
  const keys = new Set<string | null>()
  inputFields.forEach((item, index) => {
    const rules = fieldValidations[index]
    if (
      !rules ||
      rules.length === 0 ||
      !isEntityMaskData(item) ||
      item.unrendered ||
      item.disabled
    ) {
      return
    }
    const fails = rules.some((rule) => {
      try {
        return rule(item.value) !== true
      } catch {
        return false
      }
    })
    if (fails) {
      keys.add(layout.tabKeyByIndex[index] ?? null)
    }
  })
  return [...keys]
}

// The tab to show after a failed Save. Stay put when the user can already see an
// error -- on the active tab, or in the always-shown area above the tabs --
// otherwise move to the first tab (in tab order) that has one.
export function resolveTabForErrors({
  visibleTabs,
  activeTabKey,
  tabKeysWithErrors,
}: {
  visibleTabs: IMaskTab[]
  activeTabKey: string | null
  tabKeysWithErrors: (string | null)[]
}): string | null {
  if (
    tabKeysWithErrors.includes(null) ||
    tabKeysWithErrors.includes(activeTabKey)
  ) {
    return activeTabKey
  }
  const firstWithError = visibleTabs.find((tab) =>
    tabKeysWithErrors.includes(tab.key)
  )
  return firstWithError ? firstWithError.key : activeTabKey
}
